import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardTitle } from '@shadcn/components/ui/card';
import { Row } from '@shadcn/components/ui/row';
import { Col } from '@shadcn/components/ui/col';
import useQuery from '../../../helpers/JIFI/useQuery';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import LocationOrderTable from './components/LocationOrderTable';
import CreateConfigModal from './components/CreateConfigModal';
import EditConfigModal from './components/EditConfigModal';
import { Button } from '@shadcn/components/ui/button';
import { Dialog, DialogTrigger } from '@shadcn/components/ui/dialog';
import { REGION_CODES } from '../constants/regions';

const getRegionSortOrder = (region) => {
  if (!region || region === 'ALL') return REGION_CODES.length;
  const regionIndex = REGION_CODES.indexOf(region);
  return regionIndex === -1 ? REGION_CODES.length + 1 : regionIndex;
};

const LocationOrderConfig = () => {
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);

  // Fetch location regions
  const [locationsByRegion, locationRegionState] = useQuery({
    url: '/inventory/getLocationRegion',
    refresh,
  });

  // Fetch location order configs
  const [locationOrderConfigs, locationOrderState] = useQuery({
    url: '/locationOrderConfig',
    refresh,
  });

  const handleCreateConfig = () => {
    setRefresh(prev => prev + 1);
    setIsCreateModalOpen(false);
  };

  const handleUpdateConfig = () => {
    setRefresh(prev => prev + 1);
    setIsEditModalOpen(false);
    setSelectedConfig(null);
  };

  const handleEditConfig = (config) => {
    setSelectedConfig(config);
    setIsEditModalOpen(true);
  };

  const processedConfigs = useMemo(() => {
    if (!locationOrderConfigs) return [];
    
    return locationOrderConfigs
      .filter(config => {
        if (config.type === 'FE' && config.region && config.region !== 'ALL') {
          return REGION_CODES.includes(config.region);
        }

        if (!config.region || config.region === 'ALL') {
          return true;
        }

        return false;
      })
      .sort((a, b) => getRegionSortOrder(a.region) - getRegionSortOrder(b.region));
  }, [locationOrderConfigs]);

  const isLoading = locationRegionState.loading || locationOrderState.loading;

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Settings" breadcrumbItem="Location Order Config" />
          <div className="flex justify-center items-center h-64">
            <div className="spinner-border text-primary m-1" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Settings" breadcrumbItem="Location Order Config" />
        
        <Row>
          <Col>
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <CardTitle className="text-lg font-semibold">
                    Location Order Configuration
                  </CardTitle>
                  
                  <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
                    <DialogTrigger asChild>
                      <Button variant="default">
                        <i className="bx bx-plus mr-2"></i>
                        Add New Config
                      </Button>
                    </DialogTrigger>
                    <CreateConfigModal
                      locationsByRegion={locationsByRegion}
                      onSuccess={handleCreateConfig}
                      isOpen={isCreateModalOpen}
                      onClose={() => setIsCreateModalOpen(false)}
                    />
                  </Dialog>
                </div>

                <LocationOrderTable
                  configs={processedConfigs}
                  locationsByRegion={locationsByRegion}
                  onEdit={handleEditConfig}
                  refresh={refresh}
                  onRefresh={() => setRefresh(prev => prev + 1)}
                />
              </CardContent>
            </Card>
          </Col>
        </Row>

        {selectedConfig && (
          <EditConfigModal
            config={selectedConfig}
            locationsByRegion={locationsByRegion}
            onSuccess={handleUpdateConfig}
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedConfig(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default LocationOrderConfig; 
